import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Music, Upload, Trash2, Edit2, Play, Pause, Loader2, Search,
  Plus, FileAudio, BookOpen, Clock, ChevronDown, X, Save, Mic
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const WorshipSongsAdmin = ({ api }) => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editSong, setEditSong] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterWeek, setFilterWeek] = useState('');
  const [playingSongId, setPlayingSongId] = useState(null);
  const audioRef = useRef(null);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '', artist: '', album: '', lyrics: '', bible_verse: '',
    week_number: 0, is_general: true, order: 0,
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => { fetchSongs(); }, []);

  const fetchSongs = async () => {
    try {
      setLoading(true);
      const r = await api.get('/christian/worship-songs');
      setSongs(r.data.songs || []);
    } catch (e) {
      toast.error('Failed to fetch songs');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return toast.error('Please select an audio file');
    if (!uploadForm.title.trim()) return toast.error('Please enter a song title');

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadForm.title);
      formData.append('artist', uploadForm.artist);
      formData.append('album', uploadForm.album);
      formData.append('lyrics', uploadForm.lyrics);
      formData.append('bible_verse', uploadForm.bible_verse);
      formData.append('week_number', uploadForm.week_number);
      formData.append('is_general', uploadForm.is_general);
      formData.append('order', uploadForm.order);

      await api.post('/christian/worship-songs/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Song uploaded successfully!');
      setUploadDialog(false);
      resetUploadForm();
      fetchSongs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editSong) return;
    try {
      await api.put(`/christian/worship-songs/${editSong.id}`, {
        title: editSong.title,
        artist: editSong.artist,
        album: editSong.album,
        lyrics: editSong.lyrics,
        bible_verse: editSong.bible_verse,
        week_number: editSong.week_number || 0,
        is_general: editSong.is_general,
        order: editSong.order,
      });
      toast.success('Song updated!');
      setEditDialog(false);
      fetchSongs();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const handleDelete = async (songId) => {
    if (!window.confirm('Delete this song?')) return;
    try {
      await api.delete(`/christian/worship-songs/${songId}`);
      toast.success('Song deleted');
      if (playingSongId === songId) stopPlayback();
      fetchSongs();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const togglePlay = (song) => {
    if (playingSongId === song.id) {
      stopPlayback();
    } else {
      stopPlayback();
      const token = localStorage.getItem('token');
      const audioUrl = `${API_URL}/api/christian/worship-songs/${song.id}/stream`;
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.load();
        // Add auth header via fetch for streaming
        fetch(audioUrl, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.blob())
          .then(blob => {
            const url = URL.createObjectURL(blob);
            audioRef.current.src = url;
            audioRef.current.play();
            setPlayingSongId(song.id);
          })
          .catch(() => toast.error('Failed to play song'));
      }
    }
  };

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setPlayingSongId(null);
  };

  const resetUploadForm = () => {
    setUploadForm({ title: '', artist: '', album: '', lyrics: '', bible_verse: '', week_number: 0, is_general: true, order: 0 });
    setSelectedFile(null);
  };

  const autoGenerateTimestamps = async (songId) => {
    try {
      await api.post(`/christian/worship-songs/${songId}/auto-timestamps`);
      toast.success('Lyrics timestamps generated!');
      fetchSongs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate timestamps');
    }
  };

  const filtered = songs.filter(s => {
    const matchSearch = !searchQuery || s.title?.toLowerCase().includes(searchQuery.toLowerCase()) || s.artist?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchWeek = !filterWeek || (filterWeek === 'general' ? s.is_general : s.week_number === parseInt(filterWeek));
    return matchSearch && matchWeek;
  });

  return (
    <div className="space-y-6">
      <audio ref={audioRef} onEnded={() => setPlayingSongId(null)} />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Music className="w-6 h-6 text-purple-500" /> Worship Songs
          </h2>
          <p className="text-slate-500 text-sm">{songs.length} songs in library</p>
        </div>
        <Button onClick={() => { resetUploadForm(); setUploadDialog(true); }}
          className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white" data-testid="upload-song-btn">
          <Upload className="w-4 h-4 mr-2" /> Upload Song
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by title or artist..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)} className="pl-9"
            data-testid="song-search" />
        </div>
        <select value={filterWeek} onChange={e => setFilterWeek(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="song-week-filter">
          <option value="">All Songs</option>
          <option value="general">General Library</option>
          {Array.from({ length: 52 }, (_, i) => (
            <option key={i + 1} value={i + 1}>Week {i + 1}</option>
          ))}
        </select>
      </div>

      {/* Songs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-slate-500">
            <FileAudio className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">{songs.length === 0 ? 'No songs uploaded yet' : 'No songs match your filters'}</p>
            <p className="text-sm mt-1">Upload worship songs for children to enjoy during their lessons.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((song, i) => (
            <Card key={song.id} className={`transition-all hover:shadow-md ${playingSongId === song.id ? 'border-purple-400 bg-purple-50' : ''}`}
              data-testid={`song-row-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Play button */}
                  <button onClick={() => togglePlay(song)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                      playingSongId === song.id
                        ? 'bg-purple-500 text-white shadow-md'
                        : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                    }`}
                    data-testid={`play-song-${i}`}>
                    {playingSongId === song.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                  </button>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{song.title}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                      {song.artist && <span>{song.artist}</span>}
                      {song.album && <><span>·</span><span>{song.album}</span></>}
                      <span>·</span>
                      <span>{formatFileSize(song.file_size || 0)}</span>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {song.week_number > 0 && (
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        Week {song.week_number}
                      </Badge>
                    )}
                    {song.is_general && (
                      <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                        General
                      </Badge>
                    )}
                    {song.bible_verse && (
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        <BookOpen className="w-3 h-3 mr-1" />{song.bible_verse}
                      </Badge>
                    )}
                    {song.lyrics_timestamps?.length > 0 && (
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                        <Mic className="w-3 h-3 mr-1" />Sing-Along
                      </Badge>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 flex-shrink-0">
                    {song.lyrics && !song.lyrics_timestamps?.length && (
                      <Button variant="ghost" size="icon" title="Auto-generate sing-along timestamps"
                        onClick={() => autoGenerateTimestamps(song.id)}
                        data-testid={`auto-timestamps-${i}`}>
                        <Mic className="w-4 h-4 text-purple-400" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => { setEditSong({ ...song }); setEditDialog(true); }}
                      data-testid={`edit-song-${i}`}>
                      <Edit2 className="w-4 h-4 text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(song.id)}
                      data-testid={`delete-song-${i}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-500" /> Upload Worship Song
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* File picker */}
            <div>
              <Label>Audio File *</Label>
              <div className="mt-1 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-purple-300 transition-all"
                onClick={() => document.getElementById('song-file-input').click()}>
                <input id="song-file-input" type="file" accept="audio/*" className="hidden"
                  onChange={e => {
                    const f = e.target.files[0];
                    if (f) {
                      setSelectedFile(f);
                      if (!uploadForm.title) setUploadForm(p => ({ ...p, title: f.name.replace(/\.[^.]+$/, '') }));
                    }
                  }} />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileAudio className="w-5 h-5 text-purple-500" />
                    <span className="font-medium text-slate-700">{selectedFile.name}</span>
                    <span className="text-xs text-slate-400">({formatFileSize(selectedFile.size)})</span>
                    <button onClick={e => { e.stopPropagation(); setSelectedFile(null); }}>
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <FileAudio className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">Click to select audio file</p>
                    <p className="text-xs text-slate-400 mt-1">MP3, WAV, OGG, AAC, M4A, FLAC</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Title *</Label>
                <Input value={uploadForm.title} onChange={e => setUploadForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Amazing Grace" data-testid="upload-title" />
              </div>
              <div>
                <Label>Artist</Label>
                <Input value={uploadForm.artist} onChange={e => setUploadForm(p => ({ ...p, artist: e.target.value }))}
                  placeholder="John Newton" data-testid="upload-artist" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Album</Label>
                <Input value={uploadForm.album} onChange={e => setUploadForm(p => ({ ...p, album: e.target.value }))}
                  placeholder="Hymns for Kids" data-testid="upload-album" />
              </div>
              <div>
                <Label>Bible Verse Reference</Label>
                <Input value={uploadForm.bible_verse} onChange={e => setUploadForm(p => ({ ...p, bible_verse: e.target.value }))}
                  placeholder="Psalm 100:1" data-testid="upload-verse" />
              </div>
            </div>

            <div>
              <Label>Lyrics</Label>
              <Textarea value={uploadForm.lyrics} onChange={e => setUploadForm(p => ({ ...p, lyrics: e.target.value }))}
                placeholder="Enter song lyrics..." rows={3} data-testid="upload-lyrics" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assign to Week (0 = none)</Label>
                <Input type="number" min={0} max={52} value={uploadForm.week_number}
                  onChange={e => setUploadForm(p => ({ ...p, week_number: parseInt(e.target.value) || 0 }))}
                  data-testid="upload-week" />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input type="number" min={0} value={uploadForm.order}
                  onChange={e => setUploadForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                  data-testid="upload-order" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={uploadForm.is_general}
                onCheckedChange={v => setUploadForm(p => ({ ...p, is_general: v }))}
                data-testid="upload-general-toggle" />
              <Label>Add to General Library (available in all weeks)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFile}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white" data-testid="confirm-upload-btn">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-purple-500" /> Edit Song
            </DialogTitle>
          </DialogHeader>
          {editSong && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Title</Label>
                  <Input value={editSong.title || ''} onChange={e => setEditSong(p => ({ ...p, title: e.target.value }))}
                    data-testid="edit-title" />
                </div>
                <div>
                  <Label>Artist</Label>
                  <Input value={editSong.artist || ''} onChange={e => setEditSong(p => ({ ...p, artist: e.target.value }))}
                    data-testid="edit-artist" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Album</Label>
                  <Input value={editSong.album || ''} onChange={e => setEditSong(p => ({ ...p, album: e.target.value }))} />
                </div>
                <div>
                  <Label>Bible Verse</Label>
                  <Input value={editSong.bible_verse || ''} onChange={e => setEditSong(p => ({ ...p, bible_verse: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Lyrics</Label>
                <Textarea value={editSong.lyrics || ''} onChange={e => setEditSong(p => ({ ...p, lyrics: e.target.value }))}
                  rows={4} data-testid="edit-lyrics" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Week Number (0 = none)</Label>
                  <Input type="number" min={0} max={52} value={editSong.week_number || 0}
                    onChange={e => setEditSong(p => ({ ...p, week_number: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input type="number" min={0} value={editSong.order || 0}
                    onChange={e => setEditSong(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={editSong.is_general || false}
                  onCheckedChange={v => setEditSong(p => ({ ...p, is_general: v }))} />
                <Label>General Library</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white"
              data-testid="confirm-edit-btn">
              <Save className="w-4 h-4 mr-2" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorshipSongsAdmin;
